import { CalendarOutlined } from "@ant-design/icons";
import { Button, Divider, Space, Tooltip, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import dynamic from "next/dynamic";
import React from "react";
import { FormattedMessage, defineMessages, useIntl } from "react-intl";

import {
  CropFieldGroupFormSelect,
  LocalityFormSelect,
} from "../../components/agro";
import { Form } from "../../components/form";
import { ItemCategorySelect } from "../../components/itemCategories";
import { ListMenu, ListView } from "../../components/shared";
import { DateFilterInput } from "../../components/shared/ListView/DateFilterInput";
import { Statistic } from "../../components/shared/Statistic";
import { WarehouseSelect } from "../../components/warehouses";
import BasicLayout from "../../layouts/BasicLayout";
import {
  formatDateRange,
  formatDateTime,
  formatDocumentLink,
  formatInventoryEntryCode,
  formatInventoryStatus,
  formatMoneyAmount,
  formatVariantLink,
  formatVariantQuantity,
  formatWarehouse,
  useCurrencySwitch,
} from "../../lib/formats";
import {
  DateRangeType,
  InventoryMovementFragment,
  InventoryMovementLocationFragment,
} from "../../lib/graphql";
import {
  useCurrentUser,
  useIngredientOptions,
  useInventoryDocumentTypeFilterOptions,
  useInventoryEntryCodeOptions,
  useInventoryMovementReport,
  useItemOptions,
  usePermissions,
  useRouterFilter,
} from "../../lib/hooks";
import { useMediaXs } from "../../lib/hooks/basic/media";
import { useVariantOptions } from "../../lib/hooks/inventory/variants";
import { useReportBreadcrumbs } from "../../lib/hooks/reports/common";
import routes from "../../lib/routes";
import { filterFalse } from "../../lib/utils";

const InventoryMovementPie = dynamic(
  () => import("../../components/reports/inventory/InventoryMovementPie"),
  { ssr: false }
);

const messages = defineMessages({
  inventoryMovements: {
    id: "inventoryMovements",
    defaultMessage: "inventoryMovements",
  },
  description: {
    id: "inventoryMovements.description",
    defaultMessage: "description",
  },
  pageTitle: {
    id: "inventoryMovements.pageTitle",
    defaultMessage: "Inventory Movements",
  },
});

function Location({
  movement,
  location,
}: {
  movement: InventoryMovementFragment;
  location?: InventoryMovementLocationFragment;
}) {
  const { currentTenant } = useCurrentUser();

  if (!location) return null;

  return (
    <Space orientation="vertical" size={0}>
      <Typography.Text ellipsis>
        {location.warehouse && formatWarehouse(location.warehouse)}
      </Typography.Text>
      <Typography.Text
        strong
        type={location.amount > 0 ? "success" : "warning"}
      >
        {formatVariantQuantity(movement.variant, Math.abs(location.amount))}
      </Typography.Text>
      {currentTenant.features.inventoryStock &&
      location.startingStock != null &&
      location.endingStock != null ? (
        <Typography.Text type="secondary" style={{ fontSize: "80%" }}>
          {formatVariantQuantity(movement.variant, location.startingStock)} →{" "}
          {formatVariantQuantity(movement.variant, location.endingStock)}
        </Typography.Text>
      ) : null}
    </Space>
  );
}

export default function InventoryMovementReport() {
  const intl = useIntl();
  const { currentLocalityIds: localityId } = useCurrentUser();
  const documentTypeOptions = useInventoryDocumentTypeFilterOptions();
  const breadcrumbs = useReportBreadcrumbs({
    path: routes.reports.inventoryMovements(),
    breadcrumbName: intl.formatMessage(messages.inventoryMovements),
  });
  const { currentCurrency, currencySwitch } = useCurrencySwitch();
  const { filter } = useRouterFilter();
  const mode = String(filter.mode);
  const showCost = usePermissions((p) => !!p.settings?.showCost);
  const isXs = useMediaXs();
  const divider = !isXs && (
    <Divider orientation="vertical" style={{ height: 40, margin: 0, top: 0 }} />
  );
  const today = dayjs().tz().startOf("day");
  const changeDates = intl.formatMessage({
    id: "inventoryMovements.changeDates",
    defaultMessage: "Change dates",
  });
  const defaultDocumentDate = {
    rangeType: DateRangeType.Range,
    range: [today.startOf("year"), today],
  };

  const columns: ColumnsType<InventoryMovementFragment> = filterFalse([
    {
      title: <FormattedMessage id="documentDate" />,
      dataIndex: "documentDate",
      sorter: true,
      width: "12rem",
      render: (_, e) => formatDateTime(e.documentDate),
    },
    {
      title: <FormattedMessage id="document" defaultMessage="document" />,
      dataIndex: "documentId",
      sorter: true,
      width: "12rem",
      render: (_, e) =>
        formatDocumentLink(e.documentId, e.documentType, e.entryCode),
    },
    {
      title: <FormattedMessage id="internalId" />,
      dataIndex: "documentInternalId",
      width: "7rem",
    },
    {
      title: <FormattedMessage id="variants.entityName" />,
      dataIndex: "variant",
      width: "18rem",
      render: (_, e) => formatVariantLink(e.variant),
    },
    {
      title: <FormattedMessage id="type" />,
      align: "center",
      render: (_, e) => formatInventoryEntryCode(e.entryCode),
    },
    {
      title: (
        <FormattedMessage
          id="inventoryMovements.entry"
          defaultMessage="Entry"
        />
      ),
      width: "21rem",
      align: "center",
      render: (_, e) => (
        <Location
          movement={e}
          location={
            e.reverted ? e.locations[0] : e.locations.find((l) => l.amount >= 0)
          }
        />
      ),
    },
    {
      title: (
        <FormattedMessage id="inventoryMovements.exit" defaultMessage="Exit" />
      ),
      width: "21rem",
      align: "center",
      render: (_, e) => (
        <Location
          movement={e}
          location={
            e.reverted ? e.locations[0] : e.locations.find((l) => l.amount < 0)
          }
        />
      ),
    },
    showCost && {
      title: <FormattedMessage id="unitCost" />,
      align: "right",
      render: (_, e) =>
        e.reverted
          ? "—"
          : formatMoneyAmount(e.unitCost, currentCurrency.isoCode, {
              maximumFractionDigits: 4,
            }),
      width: "8rem",
    },
    showCost && {
      title: (
        <FormattedMessage
          id="totalDocumentValue"
          defaultMessage="totalDocumentValue"
        />
      ),
      align: "right",
      render: (_, e) =>
        e.reverted
          ? "—"
          : formatMoneyAmount(
              Math.abs(e.documentCost || 0),
              currentCurrency.isoCode
            ),
      width: "12rem",
    },
  ]);

  if (mode == "reserved") {
    columns.splice(
      3,
      3,
      {
        title: <FormattedMessage id="workOrders.inventoryStatus" />,
        align: "center",
        width: "11rem",
        render: (_, e) => formatInventoryStatus(e.entryCode),
      },
      {
        title: <FormattedMessage id="quantity" />,
        align: "center",
        width: "12rem",
        render: (_, e) => {
          const location = e.locations.find((l) => l.amount >= 0);
          return (
            location &&
            formatVariantQuantity(e.variant, Math.abs(location.amount))
          );
        },
      }
    );
  }

  return (
    <BasicLayout
      title={intl.formatMessage(messages.pageTitle)}
      breadcrumbs={breadcrumbs}
      tooltip={intl.formatMessage(messages.description)}
      headerContent={
        <ListMenu
          exportType="InventoryMovementReport"
          exportParams={{ filter: { currencyCode: currentCurrency.isoCode } }}
          allowNew={false}
          right={currencySwitch}
        />
      }
      authorize={(p) => !!p?.report?.inventoryMovement}
      fullContentHeight={false}
    >
      <ListView
        query={useInventoryMovementReport}
        queryOptions={{
          variables: {
            filter: {
              localityId,
              currencyCode: currentCurrency.isoCode,
              mode,
              documentDate: defaultDocumentDate,
            },
            showCost,
          },
        }}
        tableProps={{ fixedHeader: true }}
        renderBeforeTable={(tableFilter, totalCount, openFilters) => {
          const documentDate = tableFilter.documentDate;
          const range = documentDate?.range;

          return (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: "16px 32px",
                marginBottom: 20,
              }}
            >
              <div>
                <Typography.Text type="secondary">
                  <FormattedMessage id="documentDate" />
                </Typography.Text>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <Typography.Title
                    level={4}
                    style={{ margin: 0, fontSize: 20 }}
                  >
                    {range?.[0] && range?.[1] ? (
                      formatDateRange(
                        dayjs(range[0]).tz().format("YYYY-MM-DD"),
                        dayjs(range[1]).tz().format("YYYY-MM-DD")
                      )
                    ) : documentDate?.rangeType ? (
                      intl.formatMessage({
                        id: `dateRanges.types.${documentDate.rangeType}`,
                      })
                    ) : (
                      <FormattedMessage
                        id="inventoryMovements.allDates"
                        defaultMessage="All dates"
                      />
                    )}
                  </Typography.Title>
                  <Tooltip title={changeDates}>
                    <Button
                      type="text"
                      size="small"
                      icon={<CalendarOutlined />}
                      aria-label={changeDates}
                      onClick={() => openFilters("documentDate")}
                    />
                  </Tooltip>
                </div>
              </div>
              {divider}
              <div>
                <Statistic
                  style={{ margin: 0, fontSize: 20 }}
                  title={
                    <FormattedMessage
                      id="inventoryMovements.totalMovements"
                      defaultMessage="Total movements"
                    />
                  }
                >
                  {totalCount == null ? "—" : intl.formatNumber(totalCount)}
                </Statistic>
              </div>
              <div style={{ marginInlineStart: isXs ? 0 : "auto" }}>
                <InventoryMovementPie filter={tableFilter} />
              </div>
            </div>
          );
        }}
        disabledRecord={(e) => e.reverted}
        columns={columns}
        filters={[
          {
            title: <FormattedMessage id="variants.entityName" />,
            name: "variantId",
            type: "select",
            multiple: true,
            optionsHook: useVariantOptions,
          },
          {
            title: <FormattedMessage id="items.entityName" />,
            name: "itemId",
            type: "select",
            multiple: true,
            optionsHook: useItemOptions,
          },
          {
            name: "localityId",
            type: "custom",
            initialValue: localityId,
            render: () => <LocalityFormSelect />,
          },
          {
            title: <FormattedMessage id="itemCategories.entityName" />,
            name: "itemCategoryId",
            type: "custom",
            render: () => <ItemCategorySelect multiple />,
          },
          {
            title: <FormattedMessage id="ingredients.entityName" />,
            name: "ingredientId",
            type: "select",
            multiple: true,
            optionsHook: useIngredientOptions,
          },
          {
            title: <FormattedMessage id="internalId" />,
            name: "internalId",
            type: "string",
          },
          {
            title: <FormattedMessage id="warehouses.entityName" />,
            name: "warehouseId",
            type: "custom",
            render: () => (
              <Form.Item
                noStyle
                shouldUpdate={(prev, next) =>
                  prev.localityId !== next.localityId
                }
              >
                {({ getFieldValue }) => (
                  <Form.Item noStyle name="warehouseId">
                    <WarehouseSelect
                      clearOnLocalityChange
                      mode="multiple"
                      placeholder=""
                      localityIds={getFieldValue("localityId")}
                    />
                  </Form.Item>
                )}
              </Form.Item>
            ),
          },
          {
            name: "cropFieldGroup",
            type: "custom",
            render: () => <CropFieldGroupFormSelect />,
          },
          {
            title: <FormattedMessage id="type" />,
            name: "entryCode",
            type: "select",
            multiple: true,
            optionsHook: useInventoryEntryCodeOptions,
          },
          {
            title: <FormattedMessage id="document" defaultMessage="document" />,
            name: "documentId",
            type: "number",
          },
          {
            title: <FormattedMessage id="documentType" />,
            name: "documentType",
            type: "select",
            multiple: true,
            options: documentTypeOptions,
          },
          {
            title: <FormattedMessage id="documentDate" />,
            name: "documentDate",
            type: "custom",
            render: () => <DateFilterInput asStringDate />,
            initialValue: defaultDocumentDate,
            persistDefault: true,
          },
        ]}
        sidebarActions={{}}
      />
    </BasicLayout>
  );
}
